import { Alert, Badge, Button, Code, Group, Loader, Modal, Paper, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { enumColor, enumLabel } from '../../lib/enums';
import { formatMoney } from '../../lib/format';

/**
 * What a delivery platform sent with an order that this system does not know how to record.
 *
 * Until this existed the answer to "what happens when someone orders the large one with no onions"
 * was: we do not know, and we would not find out. The note and the chosen options were read off the
 * platform's payload by nothing, dropped by the parser, and the order landed as a plain line under
 * the base dish — the full payload kept on file, and not one thing on any screen to suggest there
 * was anything in it worth opening.
 *
 * So this is deliberately a READER and not a model. It shows what arrived, in the shape it arrived
 * in, and stops. Nothing here parses the options into fields, because no order carrying options has
 * ever been seen — every captured line had none — and a shape guessed from nothing would be wrong
 * in a way that is expensive to undo once a screen and a report depend on it.
 */

const LINES_PATH = '/admin/v1/channels/platform-order-lines';

interface PlatformLine {
  id?: string;
  platformName?: string;
  qty?: number;
  lineTotalMinor?: number;
  currency?: string;
  /** Their JSON exactly as it arrived. Nothing in this system claims to know its shape. */
  platformOptions?: string | null;
  platformInstruction?: string | null;
  /** The names of fields on their line that nothing here reads. Never shown as they are written. */
  unreadFields?: string[] | null;
}

export interface PlatformOrderRow {
  orderId?: string;
  platformOrderNumber?: string;
  currency?: string;
  attentionFlags?: string[] | null;
  unexplainedMinor?: number | null;
}

export const attentionFlagsOf = (row: Record<string, unknown>): string[] =>
  Array.isArray(row.attentionFlags) ? (row.attentionFlags as string[]) : [];

/**
 * An order the guest took back is not money at risk, however odd its arithmetic was.
 *
 * Nothing was sold and the platform owes nothing — the receivable was reversed to zero — so adding
 * its gap into a figure headed "paid by customers but not on any item" is a figure that describes
 * no money. The same rule the unmatched-lines total already follows, for the same reason. The order
 * still says on its own row what it carried, because what arrived is still worth reading.
 */
const nothingAtRisk = (row: Record<string, unknown>) =>
  row.status === 'cancelledByPlatform' || row.status === 'rejected';

/**
 * Readable words out of a structure nobody has a specification for.
 *
 * Every text and number anywhere inside their options, in the order it appears — because whatever
 * shape they use, the words the guest picked are in there somewhere, and a reader that insisted on
 * `{ name, value }` would show nothing at all the day it turns out to be `{ label, choice }`. Keys
 * that are plainly machinery are skipped so the line reads as choices rather than as a database
 * dump; the exact text is shown underneath regardless, so nothing depends on this being right.
 */
const MACHINERY = /(^|_)(id|ids|uuid|sku|key|slug|url|image|created|updated)(_|$)/i;

function readableChoices(optionsJson: string | null | undefined): string[] {
  if (!optionsJson) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(optionsJson);
  } catch {
    // Not JSON at all. Their text is still their text — show it whole rather than nothing.
    return [optionsJson];
  }
  const found: string[] = [];
  const walk = (value: unknown, key: string) => {
    if (value === null || value === undefined || value === '') return;
    if (Array.isArray(value)) {
      for (const entry of value) walk(entry, key);
      return;
    }
    if (typeof value === 'object') {
      for (const [childKey, child] of Object.entries(value)) walk(child, childKey);
      return;
    }
    if (MACHINERY.test(key)) return;
    if (typeof value === 'boolean') return;
    const text = String(value).trim();
    if (text !== '' && !found.includes(text)) found.push(text);
  };
  walk(parsed, '');
  return found;
}

/** One of their lines, with everything that came attached to it. */
function LineDetail({ line }: { line: PlatformLine }) {
  const choices = readableChoices(line.platformOptions);
  return (
    <Paper withBorder p="sm" radius="md">
      <Group
        justify="space-between"
        wrap="nowrap"
        mb={choices.length > 0 || line.platformInstruction ? 8 : 0}
      >
        <Text fw={600}>
          {line.qty && line.qty > 1 ? `${line.qty} × ` : ''}
          {line.platformName}
        </Text>
        <Text size="sm" c="dimmed">
          {formatMoney(line.lineTotalMinor, line.currency ?? 'USD')}
        </Text>
      </Group>

      {choices.length > 0 && (
        <Stack gap={4} mb="xs">
          <Text size="xs" c="dimmed">
            What they chose
          </Text>
          <Group gap={6}>
            {choices.map((choice) => (
              <Badge key={choice} variant="light" color="blue">
                {choice}
              </Badge>
            ))}
          </Group>
          {/* Their own text underneath, always. The badges above are this screen's reading of a
              structure nobody has a specification for; this is the thing itself, and it is what
              settles an argument about what a guest actually asked for. */}
          <Code block style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {line.platformOptions}
          </Code>
        </Stack>
      )}

      {line.platformInstruction && (
        <Stack gap={2} mb={line.unreadFields?.length ? 'xs' : 0}>
          <Text size="xs" c="dimmed">
            What they wrote
          </Text>
          <Text size="sm">“{line.platformInstruction}”</Text>
        </Stack>
      )}

      {line.unreadFields && line.unreadFields.length > 0 && (
        // The names of their fields are their software's vocabulary, not English, so they are
        // counted rather than printed. What matters to the owner is that this item carried
        // something nobody is reading, and that the platform can be asked about it.
        <Text size="sm" c="orange">
          This item also carried {line.unreadFields.length}{' '}
          {line.unreadFields.length === 1 ? 'detail' : 'details'} we do not record yet.
        </Text>
      )}
    </Paper>
  );
}

/**
 * The button on the arrivals row, and what opens behind it.
 *
 * Only on the orders that carried something — a button on every row that says "nothing to see" on
 * almost all of them is a button nobody presses on the day there is.
 */
export function WhatTheyAskedForButton({ row }: { row: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);
  const flags = attentionFlagsOf(row);
  const orderId = typeof row.orderId === 'string' ? row.orderId : '';
  const unexplained = typeof row.unexplainedMinor === 'number' ? row.unexplainedMinor : null;
  const currency = typeof row.currency === 'string' ? row.currency : 'USD';

  const lines = useQuery({
    queryKey: [...listKey(LINES_PATH), orderId],
    queryFn: () => rawApi<PlatformLine[]>('GET', `${LINES_PATH}?orderId=${orderId}`),
    enabled: open && orderId !== '',
  });

  if (flags.length === 0) return null;

  return (
    <>
      <Button size="xs" variant="light" onClick={() => setOpen(true)}>
        See what they sent
      </Button>
      <Modal
        opened={open}
        onClose={() => setOpen(false)}
        size="lg"
        title={`Order ${row.platformOrderNumber ?? ''} — what the platform sent`}
      >
        <Stack gap="md">
          {/* The money first when there is money in it. An owner who opens this because a payout
              was short is not looking for a list of toppings. */}
          {unexplained !== null && unexplained !== 0 && !nothingAtRisk(row) && (
            <Alert color="red" title="The items do not add up to what the customer paid">
              <Text size="sm">
                The customer was charged {formatMoney(Math.abs(unexplained), currency)}{' '}
                {unexplained > 0 ? 'more' : 'less'} than the items on this order come to, once the platform’s
                delivery fee is taken off. Only the items are on their bill, so if that gap is an extra
                somebody paid for, this payout will be short by it. Check it against the order on the
                platform’s own screen and ask them what the difference was.
              </Text>
            </Alert>
          )}
          {unexplained === null && flags.includes('moneyUnexplained') && (
            <Alert color="orange" title="This order could not be checked">
              <Text size="sm">
                The platform put something on this order we have never seen before — a discount, a coupon, a
                saving or a charge of their own — and we do not yet know whether it comes off the items or off
                the order total. Rather than show you a figure that could be wrong either way, there is none:
                compare this order against the platform’s own screen.
              </Text>
            </Alert>
          )}
          {/* Not on an order the guest took back: nothing was sold, so nothing is owed, tip or no
              tip. The same rule the gap above and the money card already follow. */}
          {flags.includes('merchantTipNotBilled') && !nothingAtRisk(row) && (
            <Alert color="red" title="The customer left a tip for the bar">
              <Text size="sm">
                The platform collected a tip marked as yours, not the driver’s, and nothing here puts it on
                their bill — only the items go on that. Check the amount on the platform’s own screen and ask
                them whether it is paid over with the food.
              </Text>
            </Alert>
          )}

          {lines.isPending && orderId !== '' ? (
            <Group justify="center" py="lg">
              <Loader size="sm" />
            </Group>
          ) : (
            <Stack gap="xs">
              {(lines.data ?? []).map((line) => (
                <LineDetail key={line.id} line={line} />
              ))}
            </Stack>
          )}

          <Text size="xs" c="dimmed">
            Everything the platform sent with this order is kept exactly as it arrived, whether it is shown
            here or not.
          </Text>
        </Stack>
      </Modal>
    </>
  );
}

/** The reasons an order wants a look, as badges on the row. */
export function AttentionCell({ row }: { row: Record<string, unknown> }) {
  const flags = attentionFlagsOf(row);
  if (flags.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }
  return (
    <Group gap={4}>
      {flags.map((flag) => (
        <Badge key={flag} variant="light" color={enumColor('platformOrderAttention', flag)}>
          {enumLabel('platformOrderAttention', flag)}
        </Badge>
      ))}
    </Group>
  );
}

/**
 * The money, above the list, before anybody has to notice a badge.
 *
 * A line total is read from the one field of theirs that is stable across the way they price tax —
 * and whether that field includes the price of an option the guest paid for is not known. Nothing
 * guesses: the items go on the platform's bill exactly as they were sent, and what the customer
 * paid over and above them is added up here. Zero on every ordinary order, and the day it is not,
 * it is the amount a payout is about to be short by.
 */
export function MoneyThatDoesNotAddUp({ orders }: { orders: Record<string, unknown>[] }) {
  const totals = new Map<string, { currency: string; minor: number; orders: number }>();
  for (const order of orders) {
    const gap = typeof order.unexplainedMinor === 'number' ? order.unexplainedMinor : 0;
    if (gap === 0 || nothingAtRisk(order)) continue;
    const currency = typeof order.currency === 'string' ? order.currency : 'USD';
    const found = totals.get(currency) ?? { currency, minor: 0, orders: 0 };
    found.minor += gap;
    found.orders += 1;
    totals.set(currency, found);
  }
  const gaps = [...totals.values()];
  if (gaps.length === 0) return null;

  return (
    <Group gap="sm" mb="md" align="stretch">
      {gaps.map((gap) => (
        <Paper key={gap.currency} withBorder p="sm" radius="md" miw={280} maw={360}>
          <Text size="xl" fw={600} c="red">
            {formatMoney(Math.abs(gap.minor), gap.currency)}
          </Text>
          <Text size="sm" c="dimmed">
            {gap.minor > 0 ? 'paid by customers but not on any item' : 'on items nobody was charged for'} —{' '}
            {gap.orders} {gap.orders === 1 ? 'order' : 'orders'}
          </Text>
          <Text size="xs" c="dimmed" mt={6}>
            Only the items go on the platform’s bill, so a gap here is money the payout will not cover. Open
            the orders below to see what came with them.
          </Text>
        </Paper>
      ))}
    </Group>
  );
}
